import { forwardRef, useId } from 'react';
import { KNOWN_CATEGORIES } from '../../lib/categories.ts';

interface CategoryPickerProps {
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  placeholder?: string;
}

// Native combobox via <input list> + <datalist>. Dependency-free, accessible
// out of the box, and accepts free-text fallback so reviewers can type a
// category we haven't defined yet ("Other"-style rows surface in the
// confusion matrix when grading lands).
export const CategoryPicker = forwardRef<HTMLInputElement, CategoryPickerProps>(
  function CategoryPicker(
    { value, onChange, required = false, placeholder = 'Pick or type a category…' },
    ref,
  ) {
    const listId = useId();
    const isEmpty = value.trim() === '';
    const showRequiredOutline = required && isEmpty;

    return (
      <>
        <input
          ref={ref}
          type="text"
          list={listId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          aria-label="Category"
          aria-required={required}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            background: 'rgba(255,255,255,0.04)',
            border: showRequiredOutline
              ? '1px solid rgba(255,107,122,0.55)'
              : '1px solid rgba(255,255,255,0.1)',
            borderRadius: 5,
            color: '#F0F2FF',
            fontSize: 13,
            padding: '8px 10px',
            fontFamily: "'Outfit', sans-serif",
            outline: 'none',
          }}
        />
        <datalist id={listId}>
          {KNOWN_CATEGORIES.map((cat) => (
            <option key={cat.value} value={cat.value}>
              {cat.label} ({cat.group})
            </option>
          ))}
        </datalist>
      </>
    );
  },
);
